import { useState } from 'react';

import { Box } from '@mui/material';

import { EntityMetadataModel, ParameterMetadata } from '../../../../api/api';
import useAsyncValue from '../../../../common/hooks/useAsyncValue';
import { useExpressionMetadata } from '../../../../components/ExpressionMetadataContext';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { VariableScope } from '../../Models/VariableScopes/VariableScope';
import { resolveExpectedObjectType } from '../../Services/ExpectedObjectTypeResolver';
import GenericPropertyInput from '../Inputs/GenericPropertyInput';
import ExpressionComposerDialog from './ExpressionComposerDialog';
import ExpressionPlaceholder from './ExpressionPlaceholder';

type ExpressionInputProps = {
	expression: ExpressionModel | undefined;
	onExpressionChange: (expression: ExpressionModel | undefined) => void;
	parameterMetadata: ParameterMetadata;
	variableScope?: VariableScope;
	disabled?: boolean;
	/// All the parameters (needed to resolve parameter-level variableSets and expectedObjectType)
	parameters?: ParameterMetadata[];
	/// The model that contains the property value (needed for variableSet and expectedObjectType resolution)
	model?: Record<string, any>;
};

export function ExpressionInput({
	expression,
	onExpressionChange,
	parameterMetadata,
	variableScope,
	disabled,
	parameters,
	model,
}: ExpressionInputProps) {
	const { expressionScopeCalculator } = useExpressionMetadata();

	// Resolve expectedObjectType and augment variableScope with parameter-level variableSets
	const expressionMetadata = useAsyncValue(async () => {
		let expectedObjectType: EntityMetadataModel | undefined = resolveExpectedObjectType(parameterMetadata);
		let augmentedVariableScope: VariableScope | undefined = variableScope;

		if (!expressionScopeCalculator || !parameters || !model) {
			return { expectedObjectType, augmentedVariableScope };
		}

		// Resolve expectedObjectType from typeRef
		if (parameterMetadata.expectedType?.typeRef) {
			expectedObjectType = (await expressionScopeCalculator.resolveExpectedObjectType(parameterMetadata, parameters, model)) ?? expectedObjectType;
		}

		// Process parameter-level variableSets (makes variables like $event visible when editing the expression)
		if (parameterMetadata.expectedType?.variableSets && parameterMetadata.expectedType.variableSets.length > 0) {
			const newScope = new VariableScope();
			if (variableScope) {
				newScope.addPrevious(variableScope);
			}

			await expressionScopeCalculator.processVariableSets(
				parameterMetadata.expectedType.variableSets,
				parameters,
				model,
				variableScope || new VariableScope(),
				newScope,
				`parameter '${parameterMetadata.name}'`,
			);

			augmentedVariableScope = newScope;
		}

		return { expectedObjectType, augmentedVariableScope };
	}, [parameterMetadata, variableScope, expressionScopeCalculator, parameters, model]);

	const [open, setOpen] = useState(false);
	return (
		<GenericPropertyInput value={expression} onValueChange={onExpressionChange} onOpen={() => setOpen(true)} disabled={disabled} contentSx={{ minWidth: 0, overflow: 'hidden' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
				<ExpressionPlaceholder
					parameterMetadata={parameterMetadata}
					expression={expression}
					variableScope={expressionMetadata.value?.augmentedVariableScope}
					isEditable={false}
					expectedObjectType={expressionMetadata.value?.expectedObjectType}
					model={model}
				/>
			</Box>
			<ExpressionComposerDialog
				open={open}
				setOpen={setOpen}
				parameterMetadata={parameterMetadata}
				expression={expression}
				onExpressionChange={onExpressionChange}
				variableScope={expressionMetadata.value?.augmentedVariableScope}
				expectedObjectType={expressionMetadata.value?.expectedObjectType}
				model={model}
			/>
		</GenericPropertyInput>
	);
}
